<template>
  <div class="fee-schedule-table">
    <table-with-filters
      :show-load-more="false"
      :fetching="loading"
      :loading="loading"
    >
      <template #filters>
        <div class="fee-schedule-table__top-block flx flx--col mb-24 mb-16--l">
          <div
            class="fee-schedule-table__title-with-fee flx flx--col mb-8 mb-16--l"
          >
            <span class="txt--bold txt--color-04 txt--26px txt--height-34px">
              {{ $t('feeSchedule.table.title') }}
            </span>
            <div
              class="bg--color-01 sha--03 bor--radius-12 p-16 flx flx--items-center"
            >
              <span class="txt--bold txt--color-04 txt--16px txt--height-20px">
                {{ $t('feeSchedule.table.tradingFees') }}
              </span>
              <span class="txt--bold txt--22px txt--height-30px txt--main ml-4">
                {{ formattedFeeText }}
              </span>
            </div>
          </div>
          <exc-base-input
            v-model="searchCurrency"
            :placeholder="$t('feeSchedule.search')"
            size="s"
            class="fee-schedule-table__search"
          />
        </div>
      </template>
      <template #table>
        <exc-table
          :heading="columns"
          :loading="loading"
          :rows="rows"
          adaptive
          bg-in-head
          cell-size="xl"
          class="fee-schedule-table__content"
        >
          <template #td-currency="{ logo, currency }">
            <exc-coin-with-logo :name="currency" :img="logo || ''" size="s" />
          </template>
        </exc-table>
      </template>
    </table-with-filters>
  </div>
</template>

<script>
import { ExcBaseInput, ExcCoinWithLogo, ExcTable } from '@/components/common'
import { noExponents } from '@/lib/utils'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'FeeScheduleTable',
  components: {
    ExcTable,
    ExcBaseInput,
    ExcCoinWithLogo,
    TableWithFilters: () =>
      import(
        /* webpackMode: "eager" */ '@/components/ui-kit/table/TableWithFiltersNew.vue'
      ),
  },
  data () {
    return {
      searchCurrency: '',
      loading: true,
    }
  },
  computed: {
    ...mapState({
      currencyImages: state => state.pairs.currencyImages,
      fee: state => state.buySell.fee,
    }),
    ...mapGetters('currency', ['currenciesFee', 'formattedFee']),
    columns () {
      return {
        currency: this.$t('feeSchedule.tableColumns.currency'),
        depositFee: this.$t('feeSchedule.tableColumns.depositFee'),
        withdrawFee: this.$t('feeSchedule.tableColumns.withdrawalFee'),
        minDeposit: this.$t('feeSchedule.tableColumns.minDeposit'),
        minWithdraw: this.$t('feeSchedule.tableColumns.minWithdrawal'),
      }
    },
    rows () {
      if (this.loading) return []
      const val = this.currenciesFee.reduce(
        (acc, { ticker, deposit, withdraw, isFiat }) => {
          if (
            ticker &&
            ticker.toLowerCase().includes(this.searchCurrency.toLowerCase())
          ) {
            // Deposit fee column
            let depositFee =
              this.formattedFee({ ticker, method: 'deposit' }) ||
              this.$t('feeSchedule.free')
            if (ticker === 'USD') depositFee += ' / 7%' // TODO: remove when isFiat works
            if (isFiat) {
              const text = this.formattedFee({ ticker, method: 'deposit' })
              depositFee = text ? text + ' + ADV' : 'ADV'
            }

            // Withdrawal fee column
            const withdrawalFee = (() => {
              const text = this.formattedFee({ ticker, method: 'withdraw' })
              if (isFiat) {
                return text ? text + ' + ADV' : 'ADV'
              }
              return text || this.$t('feeSchedule.free')
            })()

            // Minimum Deposit column
            let minimumDeposit = `${noExponents(
              this.$filters.formatNumber(deposit.min) || '0',
            )} ${ticker}`
            if (ticker === 'USD') minimumDeposit += ' / 30 USD'

            acc.push({
              logo: this.currencyImages[ticker],
              currency: ticker,
              depositFee,
              withdrawFee: withdrawalFee,
              minDeposit: minimumDeposit,
              minWithdraw: `${noExponents(parseFloat(withdraw.min)) ||
              '0'} ${ticker}`,
            })
          }
          return acc
        },
        [],
      )

      return val
    },
    formattedFeeText () {
      return `${this.fee * 100}%`
    },
  },
  async created () {
    if (!process.client) return
    this.loading = true
    await Promise.all([
      this.getFeeSettings(),
      this.getFeeByAllCurrencies(),
    ])
    // await this.getFeeSettings()
    // await this.getFeeByAllCurrencies()
    this.loading = false
  },
  methods: {
    ...mapActions('currency', ['getFeeByAllCurrencies']),
    ...mapActions('buySell', ['getFeeSettings']),
  },
}
</script>

<style lang="scss" scoped>
.fee-schedule-table {
  @include bp(l) {
    background: color('color-01');
    box-shadow: map-get($shadows, '03');
    border-radius: toRem(12px);
    padding: toRem(24px);
  }

  &__top-block {
    width: 100%;
  }

  &__title-with-fee {
    gap: toRem(8px);

    @include bp(l) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__search {
    max-width: toRem(389px);
  }

  &__content {
    max-height: toRem(388px);
  }

  &::v-deep {
    @include bp(0, $between-l-xl) {
      .exc-table--adaptive table tr:not(.exc-table__no-items-row) td {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        align-items: flex-start;
        gap: toRem(20px);
        line-height: 1.4;

        &.exc-table__cell-currency {
          align-items: center;
        }

        &::after {
          position: static;
          white-space: nowrap;
          transform: none;
        }
      }
    }
  }
}
</style>
